import {
  driverColumnVisibility,
  moduleColumnVisibility,
  multiLengthColumnVisibility,
  emergencyDriverColumnVisibility,
} from "@/utils/ColumnMapping";
import { RTH_MODULE_CALCULATIONS } from "@/service/Constants";

/**
 * UI application state
 * @type Object
 */

const state = {
  actionBar: {
    showControls: false,
  },
  playground: {
    minimizeModuleInput: false,
    minimizeDriverInput: false,
    paneSize: 50,
    savedModuleColumnVisibility: [],
    savedDriverColumnVisibility: [],
    savedMultiLengthColumnVisibility: [],
    showTour: true,
  },
  emergencyDriver: {
    minimizeEmergencyDriver: false,
    savedEmergencyDriverColumnVisibility: [],
  },
  bodyClasses: [],
  baseLayoutMounted: false,
  dontShowChangedInputModal: false,
};

const getters = {
  showControls(state) {
    return state.actionBar.showControls;
  },
  minimizeModuleInput(state) {
    return state.playground.minimizeModuleInput;
  },
  minimizeDriverInput(state) {
    return state.playground.minimizeDriverInput;
  },
  minimizeEmergencyDriver(state) {
    return state.emergencyDriver.minimizeEmergencyDriver;
  },
  paneSize(state) {
    return state.playground.paneSize;
  },
  bodyClasses(state) {
    return state.bodyClasses;
  },
  standardModuleColumnVisibility(state, getters, rootState, rootGetters) {
    return moduleColumnVisibility(
      rootState.brain.region,
      rootGetters["brain/auth/canI"](RTH_MODULE_CALCULATIONS)
    );
  },
  standardDriverColumnVisibility(state, getters, rootState) {
    return driverColumnVisibility(rootState.brain.region);
  },
  standardMultiLengthColumnVisibility(state, getters, rootState) {
    return multiLengthColumnVisibility(rootState.brain.region);
  },
  standardEmergencyDriverColumnVisibility(state, getters, rootState) {
    return emergencyDriverColumnVisibility(rootState.brain.region);
  },
  savedModuleColumnVisibility(state) {
    return state.playground.savedModuleColumnVisibility;
  },
  savedDriverColumnVisibility(state) {
    return state.playground.savedDriverColumnVisibility;
  },
  savedMultiLengthColumnVisibility(state) {
    return state.playground.savedMultiLengthColumnVisibility;
  },
  savedEmergencyDriverColumnVisibility(state) {
    return state.emergencyDriver.savedEmergencyDriverColumnVisibility;
  },
  showTour(state) {
    return state.playground.showTour;
  },
  baseLayoutMounted(state) {
    return state.baseLayoutMounted;
  },
  dontShowChangedInputModal(state) {
    return state.dontShowChangedInputModal;
  },
};

const mutations = {
  showControls: (state, show) => {
    state.actionBar.showControls = show;
  },
  minimizeInput: (state, { type, minimize }) => {
    switch (type) {
      case "module":
        state.playground.minimizeModuleInput = minimize;
        break;
      case "driver":
        state.playground.minimizeDriverInput = minimize;
        break;
      case "emergency-driver":
        state.emergencyDriver.minimizeEmergencyDriver = minimize;
        break;
    }
  },
  setPaneSize: (state, { size }) => {
    state.playground.paneSize = Math.max(0.1, Math.min(size, 99.9));
  },
  setModuleColumnVisibility: (state, { value }) => {
    const setVisibility = (column) => {
      const index = state.playground.savedModuleColumnVisibility.findIndex(
        (col) => col.headerName === column.item.headerName
      );
      if (index !== -1) {
        state.playground.savedModuleColumnVisibility[index].value =
          column.value;
      } else {
        state.playground.savedModuleColumnVisibility.push({
          ...column.item,
          value: column.value,
        });
      }
    };

    if (value instanceof Array) {
      value.forEach(setVisibility);
    } else {
      setVisibility(value);
    }
  },
  setDriverColumnVisibility: (state, { value }) => {
    const setVisibility = (column) => {
      const index = state.playground.savedDriverColumnVisibility.findIndex(
        (col) => col.headerName === column.item.headerName
      );
      if (index !== -1) {
        state.playground.savedDriverColumnVisibility[index].value =
          column.value;
      } else {
        state.playground.savedDriverColumnVisibility.push({
          ...column.item,
          value: column.value,
        });
      }
    };

    if (value instanceof Array) {
      value.forEach(setVisibility);
    } else {
      setVisibility(value);
    }
  },
  setMultiLengthColumnVisibility: (state, { value }) => {
    const setVisibility = (column) => {
      const index = state.playground.savedMultiLengthColumnVisibility.findIndex(
        (col) => col.headerName === column.item.headerName
      );
      if (index !== -1) {
        state.playground.savedMultiLengthColumnVisibility[index].value =
          column.value;
      } else {
        state.playground.savedMultiLengthColumnVisibility.push({
          ...column.item,
          value: column.value,
        });
      }
    };

    if (value instanceof Array) {
      value.forEach(setVisibility);
    } else {
      setVisibility(value);
    }
  },
  setEmergencyDriverColumnVisibility: (state, { value }) => {
    const setVisibility = (column) => {
      const index =
        state.emergencyDriver.savedEmergencyDriverColumnVisibility.findIndex(
          (col) => col.headerName === column.item.headerName
        );
      if (index !== -1) {
        state.emergencyDriver.savedEmergencyDriverColumnVisibility[
          index
        ].value = column.value;
      } else {
        state.emergencyDriver.savedEmergencyDriverColumnVisibility.push({
          ...column.item,
          value: column.value,
        });
      }
    };

    if (value instanceof Array) {
      value.forEach(setVisibility);
    } else {
      setVisibility(value);
    }
  },

  showTour: (state, value) => {
    state.playground.showTour = value;
  },
  baseLayoutMounted: (state, value) => {
    state.baseLayoutMounted = value;
  },
  resetColumnVisibility: (state) => {
    state.playground.savedModuleColumnVisibility.map((column) => {
      column.value = true;
    });
    state.playground.savedDriverColumnVisibility.map((column) => {
      column.value = true;
    });
    state.playground.savedMultiLengthColumnVisibility.map((column) => {
      column.value = true;
    });
    state.emergencyDriver.savedEmergencyDriverColumnVisibility.map((column) => {
      column.value = true;
    });
  },
  clearSavedColumnVisibility: (state) => {
    state.playground.savedModuleColumnVisibility = [];
    state.playground.savedDriverColumnVisibility = [];
    state.playground.savedMultiLengthColumnVisibility = [];
    state.emergencyDriver.savedEmergencyDriverColumnVisibility = [];
  },
  setDontShowChangedInputModal: (state, value) => {
    state.dontShowChangedInputModal = value;
  },
};

const actions = {
  showToast({ commit }, { type, message }) {
    commit; // To make linter shut-up
    this._vm.$snotify[type](message);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
